import * as React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ClipboardList, FileQuestion, Layers, Sparkles } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  PageHeader,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  cn,
} from "@eduviaos/ui-kit";
import {
  useAssessmentQuestions,
  useAssessmentTemplates,
  useAssessmentTopics,
} from "@/hooks/useAssessments";

/**
 * Phase H5 FE-1 — IA Assessments landing.
 *
 * Hub for the F7 / F8 assessment surface: topics taxonomy, question
 * bank, and reusable templates. Templates are the unit that admissions
 * stage requirements bind to (via `template_slug` — H5 FE-3) and what
 * the public apply portal materialises into attempts (Phase F10
 * runtime).
 *
 * Live counts surface "do we have enough to wire a stage requirement?"
 * at a glance — a template needs ≥1 active topic + approved questions
 * intersecting its blueprint before activation will succeed (Phase F8
 * BE-1 pre-flight check on activation).
 */

interface AssessmentCard {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  href: string;
  count: number | null;
  countLabel: string;
}

export function AssessmentsPage() {
  const topicsQuery = useAssessmentTopics();
  const questionsQuery = useAssessmentQuestions({ limit: 200 });
  const templatesQuery = useAssessmentTemplates({ limit: 200 });

  const topicsCount = topicsQuery.data?.total ?? 0;
  const questionsTotal = questionsQuery.data?.total ?? 0;
  const approvedQuestions = React.useMemo(
    () => (questionsQuery.data?.items ?? []).filter((q) => q.status === "approved").length,
    [questionsQuery.data?.items],
  );
  const templates = templatesQuery.data?.items ?? [];
  const activeTemplates = templates.filter((t) => t.status === "active").length;
  const draftTemplates = templates.filter((t) => t.status === "draft").length;

  const isLoading = topicsQuery.isLoading || questionsQuery.isLoading || templatesQuery.isLoading;

  const cards: AssessmentCard[] = [
    {
      icon: Layers,
      title: "Topics",
      description:
        "Taxonomy used to scope every question. Blueprint items draw from one or more topics — keep them stable so historical templates stay assignable.",
      href: "/assessments/topics",
      count: topicsQuery.isLoading ? null : topicsCount,
      countLabel: "topics",
    },
    {
      icon: FileQuestion,
      title: "Question bank",
      description:
        "Every question available to the blueprint engine, drafts and approved. Filter by topic, type, difficulty, or grade. Only approved questions are drawn into attempts.",
      href: "/assessments/question-bank",
      count: questionsQuery.isLoading ? null : approvedQuestions,
      countLabel: "approved",
    },
    {
      icon: ClipboardList,
      title: "Templates",
      description:
        "Reusable blueprints + runtime config. Bind a template's slug to an admission stage requirement (admissions lifecycle) to deliver it to applicants.",
      href: "/assessments/templates",
      count: templatesQuery.isLoading ? null : activeTemplates,
      countLabel: "active",
    },
  ];

  return (
    <div className="mx-auto max-w-6xl py-8">
      <Button variant="ghost" size="sm" asChild className="mb-4">
        <Link to="/dashboard">
          <ArrowLeft aria-hidden="true" className="size-4" />
          Back to Dashboard
        </Link>
      </Button>

      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>Assessments</PageHeaderHeading>
            <PageHeaderDescription>
              Author topics, build the question bank, and assemble reusable templates that drive
              admissions-time attempts and proctored testing flows.
            </PageHeaderDescription>
          </PageHeaderTitle>
        </PageHeaderTop>
      </PageHeader>

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {cards.map((card) => (
          <Link
            key={card.title}
            to={card.href}
            className="focus-visible:ring-ring focus-visible:ring-offset-background rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            <Card
              className={cn(
                "border-border/60 hover:border-border h-full transition-colors",
                "hover:bg-muted/40",
              )}
            >
              <CardHeader>
                <div className="flex items-center gap-3">
                  <span className="bg-primary/10 text-primary grid size-9 place-items-center rounded-md">
                    <card.icon className="size-4" />
                  </span>
                  <CardTitle className="text-base">{card.title}</CardTitle>
                </div>
                <CardDescription className="mt-2 leading-snug">{card.description}</CardDescription>
              </CardHeader>
              <CardContent className="flex items-center justify-between pt-0">
                {card.count !== null ? (
                  <span className="text-muted-foreground text-sm tabular-nums">
                    <span className="text-foreground font-semibold">
                      {card.count.toLocaleString()}
                    </span>{" "}
                    {card.countLabel}
                  </span>
                ) : isLoading ? (
                  <span className="text-muted-foreground text-xs">Loading…</span>
                ) : (
                  <span className="text-muted-foreground text-xs">Open</span>
                )}
                <ArrowRight aria-hidden="true" className="text-muted-foreground size-4" />
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {!isLoading && (topicsCount === 0 || questionsTotal === 0 || activeTemplates === 0) ? (
        <Card className="mt-6 border-amber-500/30 bg-amber-500/5">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Sparkles aria-hidden="true" className="size-4 text-amber-700 dark:text-amber-300" />
              <CardTitle className="text-base text-amber-900 dark:text-amber-100">
                Finish wiring up your assessment surface
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-amber-900/80 dark:text-amber-100/80">
            <ol className="ml-4 list-decimal space-y-1">
              {topicsCount === 0 ? (
                <li>
                  Add at least one <strong>topic</strong> — every question and every blueprint item
                  pivots off a topic slug.
                </li>
              ) : null}
              {questionsTotal === 0 ? (
                <li>
                  Add (or AI-generate) <strong>questions</strong> tagged against your topics. The
                  blueprint engine only draws from approved questions.
                </li>
              ) : null}
              {activeTemplates === 0 ? (
                <li>
                  Create a <strong>template</strong> and activate it once its blueprint is
                  fulfillable. Only active templates can be bound to admission stage requirements.
                </li>
              ) : null}
            </ol>
          </CardContent>
        </Card>
      ) : null}

      {!isLoading && templates.length > 0 ? (
        <Card className="mt-6">
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="text-base">Quick stats</CardTitle>
              <Badge variant="outline" className="font-normal">
                Live
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <Stat label="Topics" value={topicsCount} />
            <Stat label="Approved questions" value={approvedQuestions} />
            <Stat label="Active templates" value={activeTemplates} />
            <Stat label="Drafts" value={draftTemplates} />
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-foreground text-2xl font-semibold tabular-nums">
        {value.toLocaleString()}
      </span>
    </div>
  );
}
